import { Dropdown, IconButton, Loader, Menu } from '@recruit-robin/robin-theme/web-components';
import { Copy_01, DotsVertical, Edit_05, PlayCircle, Trash_01, XCircle } from '@recruit-robin/robin-theme/web-icons';
import { AnimatePresence } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';

import { CampaignOverviewStatus } from 'clients/CampaignsClient/CampaignOverviewStatus.types';
import { Animated } from 'components/ui/atoms/Animated';
import { Column } from 'components/ui/atoms/Column';
import { Typography } from 'components/ui/atoms/Typography';
import { LoaderSpinner1 } from 'components/ui/atoms/icons/animated';
import { CampaignNotificationPill } from 'components/ui/molecules/CampaignNotificationPill';
import { Skeleton } from 'components/ui/molecules/Skeleton';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { useStyles } from './CampaignListItem.styles';
import { CampaignListItemProps } from './CampaignListItem.types';
import { useContainer } from './CampaignListItem.useContainer';
import { ChangeCampaignOwner } from './ChangeCampaignOwner';

const CampaignListItem = (props: CampaignListItemProps) => {
  const {
    isLoading,
    isLoadingAction,
    campaign,
    status,
    isSelected,
    handleOpenCampaign,
    handleChangeOnwer,
    handleDelete,
    handleDuplicate,
    handleToggleStatus,
    handleClickOnNewMessage,
  } = useContainer(props);
  const { dictionary } = useContext(LocalizationContext);
  const classes = useStyles();
  const [lastIsLoading, setLastIsLoading] = useState(isLoading);
  const hasNotification = campaign?.new_candidates_count || campaign?.new_replies_count;
  const isCampaignPhoneReachOutDialogOpen = useIsGlobalDialogOpen(
    GlobalDialogType.CampaignPhoneReachOut,
    campaign?.id?.toString(),
  );
  const shouldDisableActions = isCampaignPhoneReachOutDialogOpen;

  useEffect(() => {
    if (lastIsLoading !== isLoading) {
      setLastIsLoading(true);
    }
  }, [isLoading]);

  return (
    <div
      key={isLoading ? `index-${props.index}` : `${campaign.id}`}
      css={[classes.root, isLoading && classes.isLoading, isSelected && classes.selected]}
      onClick={() => handleOpenCampaign()}
      style={props.style}>
      <Column gap={0} css={classes.campaignName}>
        <AnimatePresence initial={lastIsLoading}>
          {isLoading ? (
            <Animated.FadeInOut key="loading">
              <Skeleton variant="text" width={150} textVariant="body.long" />
            </Animated.FadeInOut>
          ) : (
            <Animated.FadeInOut key="loaded">
              <Typography
                variant={(v) => (hasNotification ? v.heading[5] : v.body.long)}
                colorv2={(c) => c.neutral.variant[80]}
                css={classes.textEllipsis}>
                {campaign.name}
              </Typography>
            </Animated.FadeInOut>
          )}
        </AnimatePresence>
        <AnimatePresence initial={lastIsLoading}>
          {isLoading ? (
            <Animated.FadeInOut key="loading">
              <Skeleton variant="text" width={100} textVariant="body.long" />
            </Animated.FadeInOut>
          ) : (
            <Animated.FadeInOut key="loaded">
              <Typography variant={(v) => v.supporting.helper} colorv2={(c) => c.neutral.variant[60]}>
                {campaign.full_owner_name}
              </Typography>
            </Animated.FadeInOut>
          )}
        </AnimatePresence>
      </Column>

      {!isLoading && (
        <div css={classes.cardInfo}>
          <CampaignNotificationPill
            newCandidates={campaign.new_candidates_count}
            newMessages={campaign.new_replies_count}
            onClickNewCandidates={() => handleOpenCampaign()}
            onClickNewMessages={handleClickOnNewMessage}
          />
          <div css={classes.multiFloatingRoot} onClick={(e) => e.stopPropagation()} className="multiFloatingContainer">
            <Dropdown
              placement="bottom-end"
              shouldUsePortal={false}
              renderMenu={
                <Menu.Root customCss={classes.menuRoot}>
                  <Menu.Item.Root
                    onClick={handleDuplicate}
                    disabled={isLoadingAction.duplicate || shouldDisableActions}>
                    {isLoadingAction.duplicate ? (
                      <LoaderSpinner1 size={16} />
                    ) : (
                      <Copy_01 size={16} color={(c) => c.neutral.variant[70]} />
                    )}
                    {dictionary.duplicate}
                  </Menu.Item.Root>

                  <Menu.Item.Root onClick={handleToggleStatus} disabled={isLoadingAction.toggleStatus}>
                    {isLoadingAction.toggleStatus ? (
                      <LoaderSpinner1 size={16} />
                    ) : status === CampaignOverviewStatus.active ? (
                      <XCircle size={16} color={(c) => c.neutral.variant[70]} />
                    ) : (
                      <PlayCircle size={16} color={(c) => c.neutral.variant[70]} />
                    )}
                    {status === CampaignOverviewStatus.active ? dictionary.deactivate : dictionary.activate}
                  </Menu.Item.Root>

                  <ChangeCampaignOwner
                    currentOwnerName={campaign.full_owner_name}
                    onSelectNewOwner={handleChangeOnwer}
                    campaignId={campaign.id}
                    dropdownTriggerChildren={
                      <Menu.Item.Root disabled={isLoadingAction.changeOwner}>
                        {isLoadingAction.changeOwner ? (
                          <LoaderSpinner1 size={16} />
                        ) : (
                          <Edit_05 size={16} color={(c) => c.neutral.variant[70]} />
                        )}
                        {dictionary.changeOwner}
                      </Menu.Item.Root>
                    }
                  />

                  <Menu.Item.Root
                    onClick={handleDelete}
                    customCss={classes.deleteButton}
                    disabled={shouldDisableActions}>
                    {isLoadingAction.delete ? (
                      <Loader size={16} color={(c) => c.tertiary.red} />
                    ) : (
                      <Trash_01 size={16} />
                    )}
                    {dictionary.delete}
                  </Menu.Item.Root>
                </Menu.Root>
              }>
              {({ isOpen }) => (
                <IconButton
                  customCss={[classes.button, isOpen && classes.buttonSelected]}
                  icon={<DotsVertical size={16} />}
                  style="hover"
                  type="subtle"
                  size="small"
                  label={dictionary.more}
                />
              )}
            </Dropdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignListItem;
